import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertTriangle,
  Loader2,
  Pencil,
  Plus,
  Send,
  Trash2,
} from 'lucide-react'
import { useCan } from '@/features/auth'
import { Pagination } from '@/shared/components/ui/pagination'
import {
  deleteBroadcastTarget,
  esServidorViejo,
  fetchBroadcastLogs,
  fetchBroadcastTargets,
  probarBroadcastTarget,
  updateBroadcastTarget,
  type BroadcastLogsResult,
  type BroadcastTarget,
} from './broadcastApi'
import { DifusionTargetForm } from './DifusionTargetForm'

const LOGS_PAGE_SIZE = 10

const ESTADO_ESTILO: Record<string, string> = {
  enviado: 'bg-emerald-100 text-emerald-700',
  fallido: 'bg-red-100 text-red-700',
  omitido: 'bg-surface text-ink-subtle',
}

/** Fecha corta y legible; el registro se lee de un vistazo, no se audita. */
const cuando = (iso: string) => new Date(iso).toLocaleString('es-ES')

/**
 * Administración de la difusión por WhatsApp de las notas de versión (goal
 * `difusion-de-novedades 2`).
 *
 * Dos mitades: los **destinos** (alta, edición, interruptor, prueba y baja) y
 * el **registro de envíos**, que es lo único capaz de contestar «¿salió o no
 * salió?» — Kafka es fire-and-forget y nadie devuelve acuse.
 *
 * 🚨 El gate real es el del servidor (`IdentityAdminGuard`: Bearer de Identidad
 * + `admin_users` activo). El `useCan` de aquí y el `<Can>` de la ruta son
 * cosméticos: esconden el menú, no protegen nada.
 */
export function DifusionAdminPage() {
  const canEdit = useCan('landing:edit')
  const [destinos, setDestinos] = useState<BroadcastTarget[] | null>(null)
  const [logs, setLogs] = useState<BroadcastLogsResult | null>(null)
  const [logsPage, setLogsPage] = useState(1)
  const [editando, setEditando] = useState<BroadcastTarget | 'nuevo' | null>(
    null,
  )
  const [ocupadoId, setOcupadoId] = useState<string | null>(null)
  const [recargar, setRecargar] = useState(0)
  // El servidor todavía no tiene estos endpoints (lockstep §6.1).
  const [servidorViejo, setServidorViejo] = useState(false)

  const refrescar = useCallback(() => setRecargar((n) => n + 1), [])

  useEffect(() => {
    if (!canEdit || editando) return
    let vivo = true
    fetchBroadcastTargets()
      .then((datos) => {
        if (!vivo) return
        setDestinos(datos)
        setServidorViejo(false)
      })
      .catch((err: unknown) => {
        if (!vivo) return
        setDestinos([])
        if (esServidorViejo(err)) setServidorViejo(true)
        else toast.error('No se pudieron cargar los destinos de difusión')
      })
    return () => {
      vivo = false
    }
  }, [canEdit, editando, recargar])

  useEffect(() => {
    if (!canEdit || editando) return
    let vivo = true
    fetchBroadcastLogs({ page: logsPage, pageSize: LOGS_PAGE_SIZE })
      .then((datos) => vivo && setLogs(datos))
      // El aviso del servidor viejo ya lo da la carga de destinos: aquí se
      // degrada en silencio para no apilar dos toasts por lo mismo.
      .catch(() => vivo && setLogs(null))
    return () => {
      vivo = false
    }
  }, [canEdit, editando, logsPage, recargar])

  const conmutar = async (destino: BroadcastTarget) => {
    setOcupadoId(destino.id)
    try {
      const actualizado = await updateBroadcastTarget(destino.id, {
        activo: !destino.activo,
      })
      setDestinos((prev) =>
        (prev ?? []).map((d) => (d.id === destino.id ? actualizado : d)),
      )
      toast.success(
        actualizado.activo
          ? `${actualizado.nombre} vuelve a recibir avisos`
          : `${actualizado.nombre} deja de recibir avisos`,
      )
    } catch {
      toast.error('No se pudo cambiar el estado del destino')
    } finally {
      setOcupadoId(null)
    }
  }

  const probar = async (destino: BroadcastTarget) => {
    setOcupadoId(destino.id)
    try {
      const resultado = await probarBroadcastTarget(destino.id)
      if (resultado.estado === 'enviado') {
        toast.success(
          `Mensaje de prueba enviado a ${destino.nombre}. Comprueba que llega al grupo: «enviado» significa emitido, no entregado.`,
        )
      } else {
        toast.error(`No se pudo enviar: ${resultado.motivo ?? 'sin motivo'}`)
      }
      refrescar()
    } catch {
      toast.error('No se pudo enviar el mensaje de prueba')
    } finally {
      setOcupadoId(null)
    }
  }

  const borrar = async (destino: BroadcastTarget) => {
    setOcupadoId(destino.id)
    try {
      await deleteBroadcastTarget(destino.id)
      toast.success(`${destino.nombre} eliminado`)
      refrescar()
    } catch {
      toast.error('No se pudo eliminar el destino')
    } finally {
      setOcupadoId(null)
    }
  }

  if (!canEdit) {
    return (
      <div className="rounded-card border border-line bg-panel p-8 text-center">
        <h2 className="text-lg font-semibold text-ink">Acceso restringido</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Esta sección es exclusiva del administrador del portal.
        </p>
      </div>
    )
  }

  if (editando) {
    return (
      <DifusionTargetForm
        editing={editando === 'nuevo' ? null : editando}
        onCancel={() => setEditando(null)}
        onSaved={() => {
          setEditando(null)
          refrescar()
        }}
      />
    )
  }

  const items = destinos ?? []
  const cargando = destinos === null

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-widest text-brand">
            Administración del portal
          </span>
          <h1 className="mt-1 font-serif text-[32px] font-normal leading-[1.1] tracking-tight text-ink">
            Difusión de novedades
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            A qué grupos de WhatsApp se anuncia una nota de versión recién
            publicada. Corregir una nota ya publicada no vuelve a anunciarla.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditando('nuevo')}
          disabled={servidorViejo}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
        >
          <Plus className="size-4" /> Nuevo destino
        </button>
      </header>

      {servidorViejo && (
        <div
          role="alert"
          className="flex gap-3 rounded-card border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p>
            El servidor del portal todavía no tiene la administración de la
            difusión. Se despliega antes que esta web; vuelve a intentarlo
            cuando esté al día.
          </p>
        </div>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted">
          Destinos
        </h2>

        {cargando ? (
          <div
            role="status"
            className="flex items-center justify-center rounded-card border border-line bg-panel py-12 text-ink-muted"
          >
            <Loader2 className="mr-2 size-5 animate-spin" /> Cargando…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-card border border-dashed border-line bg-panel p-10 text-center text-sm text-ink-muted">
            {servidorViejo
              ? 'Sin datos: el servidor aún no expone esta sección.'
              : 'Aún no hay destinos. Sin destinos configurados, publicar una nota no anuncia nada.'}
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((destino) => (
              <li
                key={destino.id}
                className="flex flex-wrap items-center gap-4 rounded-card border border-line bg-panel p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-sm font-semibold text-ink">
                      {destino.nombre}
                    </h3>
                    {!destino.activo && (
                      <span className="rounded-full bg-surface px-1.5 py-0.5 text-[9px] font-bold uppercase text-ink-subtle">
                        Apagado
                      </span>
                    )}
                  </div>
                  <p className="truncate font-mono text-xs text-ink-muted">
                    {destino.destino}
                  </p>
                  <p className="truncate text-xs text-ink-subtle">
                    Cuenta {destino.chatbotId} ·{' '}
                    {destino.productos.length === 0
                      ? 'todos los productos'
                      : destino.productos.join(', ')}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={destino.activo}
                    aria-label={`Activo: ${destino.nombre}`}
                    disabled={ocupadoId === destino.id}
                    onClick={() => void conmutar(destino)}
                    className={`h-6 w-11 rounded-full transition disabled:opacity-50 ${
                      destino.activo ? 'bg-brand' : 'bg-line'
                    }`}
                  >
                    <span
                      className={`block size-5 rounded-full bg-white transition ${
                        destino.activo ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => void probar(destino)}
                    disabled={ocupadoId === destino.id}
                    className="grid size-8 place-items-center rounded-lg text-ink-muted hover:bg-surface hover:text-ink disabled:opacity-50"
                    title="Enviar mensaje de prueba"
                    aria-label={`Probar: ${destino.nombre}`}
                  >
                    <Send className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditando(destino)}
                    className="grid size-8 place-items-center rounded-lg text-ink-muted hover:bg-surface hover:text-ink"
                    title="Editar"
                    aria-label={`Editar: ${destino.nombre}`}
                  >
                    <Pencil className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void borrar(destino)}
                    disabled={ocupadoId === destino.id}
                    className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-danger-soft hover:text-danger disabled:opacity-50"
                    title="Eliminar"
                    aria-label={`Eliminar: ${destino.nombre}`}
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted">
          Últimos envíos
        </h2>
        <p className="text-xs text-ink-subtle">
          «Enviado» significa <strong>emitido</strong> hacia WhatsApp, no
          entregado: nadie devuelve acuse. Un envío de prueba se anota con el
          producto <code>(prueba)</code>.
        </p>

        {!logs || logs.items.length === 0 ? (
          <div className="rounded-card border border-dashed border-line bg-panel p-8 text-center text-sm text-ink-muted">
            Todavía no se ha anunciado nada.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-card border border-line bg-panel">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-ink-subtle">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Cuándo</th>
                    <th className="px-3 py-2 font-semibold">Producto</th>
                    <th className="px-3 py-2 font-semibold">Destino</th>
                    <th className="px-3 py-2 font-semibold">Estado</th>
                    <th className="px-3 py-2 font-semibold">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.items.map((log) => (
                    <tr key={log.id} className="border-t border-line">
                      <td className="whitespace-nowrap px-3 py-2 text-ink-muted">
                        {cuando(log.enviadoEn)}
                      </td>
                      <td className="px-3 py-2 text-ink">
                        {log.repo} {log.version}
                      </td>
                      <td className="px-3 py-2 text-ink-muted">
                        {log.target?.nombre ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                            ESTADO_ESTILO[log.estado] ?? ESTADO_ESTILO.omitido
                          }`}
                        >
                          {log.estado}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-subtle">
                        {log.motivo ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={logs.page}
              pageSize={logs.pageSize}
              totalItems={logs.total}
              onPageChange={setLogsPage}
            />
          </>
        )}
      </section>
    </div>
  )
}
